import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { SERVICES } from "@/lib/services";

const BASE = "https://nsphysiotherapy.co.za";

export const Route = createFileRoute("/services/")({
  component: ServicesIndex,
  head: () => ({
    meta: [
      { title: "Physiotherapy Services in Centurion | N S Physio" },
      { name: "description", content: "Physiotherapy services in Centurion: back pain, neck pain, sports injuries, post op, stroke rehab, home visits and dry needling. Book online." },
      { property: "og:title", content: "Physiotherapy Services in Centurion | N S Physio" },
      { property: "og:description", content: "Back pain, neck pain, sports injuries, post op rehab, stroke rehab, dry needling and home visit physiotherapy in Centurion." },
      { property: "og:url", content: `${BASE}/services` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${BASE}/services` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbJsonLd([
          { name: "Home", url: `${BASE}/` },
          { name: "Services", url: `${BASE}/services` },
        ])),
      },
    ],
  }),
});

function ServicesIndex() {
  return (
    <PageShell>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Services" }]} />
      <section className="container-page py-12 md:py-16">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Our services</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold text-primary">
            Physiotherapy services in Centurion and Midrand
          </h1>
          <p className="mt-4 text-foreground/80">
            From back and neck pain to sports injuries, post operative recovery and neurological rehabilitation, N S Physiotherapy offers evidence based, personalised care in our Centurion clinic or in your home.
          </p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <Link key={s.slug} to="/services/$slug" params={{ slug: s.slug }} className="group block rounded-2xl border border-border bg-card p-6 transition-all hover:border-primary/40 hover:shadow-[var(--shadow-soft)]">
              <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">{s.eyebrow}</div>
              <h2 className="mt-2 font-display text-lg font-bold text-primary">{s.title}</h2>
              <p className="mt-2 text-sm text-foreground/70 line-clamp-3">{s.intro}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:underline">
                Learn more <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-12 rounded-2xl border border-border bg-secondary/40 p-6 md:p-8">
          <h2 className="font-display text-xl font-bold text-primary">Why patients choose N S Physiotherapy</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm text-foreground/80">
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" /> BSc Physiotherapy (UCT), HPCSA registered</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" /> Same day appointments where possible</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" /> Medical aid claims at scheme rates</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" /> Clinic in Eco Park, Centurion plus home visits</li>
          </ul>
        </div>
        <div className="mt-6 flex flex-col items-center justify-between gap-4 rounded-2xl border border-primary/25 bg-accent/60 p-6 text-center md:flex-row md:p-8 md:text-left">
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold text-primary">Clinic Recovery Packages</h2>
            <p className="mt-2 text-sm text-foreground/75">
              Structured, consistent treatment for cash paying patients who need several sessions.
            </p>
          </div>
          <Link to="/clinic-recovery-packages" className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-7 font-semibold text-primary-foreground hover:opacity-90">
            View packages <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
